// ============================================================================
//  reportsApi.js — Frontend wrappers for /reports.php
// ============================================================================

import axios from 'axios';
import { API_BASE_URL } from '../config';

// API_BASE_URL already includes '/api' (e.g. http://localhost/warehouse-system/api)
const REPORTS = `${API_BASE_URL}/reports.php`;

const authHeaders = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const call = async (action, params = {}) => {
  try {
    const res = await axios.post(REPORTS, { action, ...params }, {
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
    });
    return res.data;
  } catch (err) {
    return {
      success: false,
      error: err.response?.data?.error || err.message || 'Network error',
    };
  }
};

// ----------------------------------------------------------------------------
// Existing endpoints (preserved for backward compat)
// ----------------------------------------------------------------------------
export const fetchOpsDashboard  = () => call('ops_dashboard');
export const fetchLotTrace      = (lotNo) => call('lot_trace', { lot_no: lotNo });
export const fetchPalletAging   = (from, to) => call('pallet_aging', { from, to });
export const fetchFclList       = (from, to) => call('fcl_list', { from, to });
export const fetchFclDetail     = (fclNo) => call('fcl_detail', { fcl_no: fclNo });

// ----------------------------------------------------------------------------
// NEW v2 endpoints
// ----------------------------------------------------------------------------
export const fetchOpsDashboardV2     = (date) => call('ops_dashboard_v2', { date });
export const fetchStockTree          = () => call('stock_tree');
export const fetchTrendAnalysis      = (from, to) => call('trend_analysis', { from, to });
export const fetchShipmentDashboard  = (period, from = null, to = null) =>
  call('shipment_dashboard', { period, ...(from && to ? { from, to } : {}) });
export const fetchShipmentTrend      = (period, from = null, to = null) =>
  call('shipment_trend', { period, ...(from && to ? { from, to } : {}) });
export const fetchShipmentTree       = (from, to) => call('shipment_tree', { from, to });
export const fetchShipmentUpcoming   = () => call('shipment_upcoming');
export const fetchShipmentRecent     = (limit = 10) => call('shipment_recent', { limit });
export const fetchPendingApprovalsV2 = () => call('pending_approvals_v2');

// ----------------------------------------------------------------------------
// Helpers
// ----------------------------------------------------------------------------
export const todayStr = () => new Date().toISOString().slice(0, 10);

export const yesterdayStr = () => {
  const d = new Date();
  d.setDate(d.getDate() - 1);
  return d.toISOString().slice(0, 10);
};

// ----------------------------------------------------------------------------
// Excel export helper using SheetJS (lazy-loaded `xlsx` package)
// Kept for backward compat with LotTraceabilityReport / PalletAgingReport / FCLManifestReport
// New code should use the ExportButtons component instead.
// ----------------------------------------------------------------------------
export const exportExcel = async ({ filename, sheets }) => {
  const XLSX = await import('xlsx');
  const wb = XLSX.utils.book_new();
  sheets.forEach(s => {
    const data = [s.headers, ...s.rows];
    const ws = XLSX.utils.aoa_to_sheet(data);
    ws['!cols'] = s.headers.map(h => ({ wch: Math.min(Math.max(String(h).length + 2, 10), 30) }));
    ws['!freeze'] = { xSplit: 0, ySplit: 1 };
    XLSX.utils.book_append_sheet(wb, ws, s.name.slice(0, 31));
  });
  XLSX.writeFile(wb, filename, { compression: true });
};
